import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const METHODS = [
  { value: 'totp',  icon: '📱', label: 'Google Authenticator', sub: 'App de autenticación (recomendado)' },
  { value: 'email', icon: '📧', label: 'Email OTP', sub: 'Código por correo electrónico' },
  { value: 'sms',   icon: '💬', label: 'SMS OTP', sub: 'Código por mensaje de texto' },
];

export default function TwoFactorSetup() {
  const [status, setStatus] = useState(null);
  const [method, setMethod] = useState('totp');
  const [step, setStep] = useState('select'); // select | setup | confirm | done
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/v1/2fa/status')
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, []);

  const inputStyle = { width: '100%', padding: '11px 14px', border: '1px solid #e2dbd0', borderRadius: 10, fontSize: 14, outline: 'none', background: '#faf8f5', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 };

  const handleSetup = async () => {
    setLoading(true); setError('');
    try {
      if (method === 'totp') {
        const res = await api.post('/api/v1/2fa/setup/totp');
        setQrCode(res.data.qr_code);
        setSecret(res.data.secret);
        setStep('setup');
      } else if (method === 'email') {
        await api.post('/api/v1/2fa/setup/email');
        setStep('confirm');
      } else if (method === 'sms') {
        if (!phone) { setError('Introduce tu número de teléfono'); setLoading(false); return; }
        await api.post(`/api/v1/2fa/setup/sms?phone=${encodeURIComponent(phone)}`);
        setStep('confirm');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al configurar 2FA');
    } finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!code || code.length < 6) { setError('Introduce el código de 6 dígitos'); return; }
    setLoading(true); setError('');
    try {
      const endpoint = method === 'totp'
        ? `/api/v1/2fa/setup/totp/confirm?code=${code}`
        : method === 'email'
        ? `/api/v1/2fa/setup/email/confirm?code=${code}`
        : `/api/v1/2fa/setup/sms/confirm?code=${code}`;
      await api.post(endpoint);
      setStep('done');
      setStatus({ enabled: true, method });
    } catch (e) {
      setError(e.response?.data?.detail || 'Código incorrecto');
    } finally { setLoading(false); }
  };

  const handleDisable = async () => {
    if (!code) { setError('Introduce el código actual para desactivar'); return; }
    if (!window.confirm('¿Seguro que quieres desactivar el 2FA? Esto reduce la seguridad de tu cuenta.')) return;
    setLoading(true); setError('');
    try {
      await api.delete(`/api/v1/2fa/disable?code=${code}`);
      setStatus({ enabled: false, method: null });
      setStep('select');
      setCode('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Código incorrecto');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Verificación en dos pasos</h1>
        <p style={{ fontSize: 13, color: '#7a7060' }}>Protege tu cuenta con 2FA</p>
      </div>

      {/* Current status */}
      {status && (
        <div style={{ background: status.enabled ? '#eaf4ef' : '#fef3e2', border: `1px solid ${status.enabled ? '#86efac' : '#fcd34d'}`, borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: status.enabled ? '#2d6a4f' : '#b45309' }}>
              {status.enabled ? '✅ 2FA Activado' : '⚠️ 2FA No activado'}
            </div>
            {status.method && <div style={{ fontSize: 12, color: status.enabled ? '#2d6a4f' : '#b45309', marginTop: 2 }}>Método: {status.method}</div>}
          </div>
          {!status.enabled && <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>Obligatorio</div>}
        </div>
      )}

      {error && <div style={{ background: '#faeef4', borderRadius: 8, padding: '9px 14px', marginBottom: 14, fontSize: 13, color: '#9b2c5e' }}>⚠ {error}</div>}

      {/* Step: Select method */}
      {step === 'select' && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 24 }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 18 }}>
            {status?.enabled ? 'Cambiar método de 2FA' : 'Activar verificación en dos pasos'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {METHODS.map(m => (
              <button key={m.value} onClick={() => setMethod(m.value)}
                style={{ padding: '14px 16px', borderRadius: 12, border: `2px solid ${method===m.value?'#c94a1e':'#e2dbd0'}`, background: method===m.value?'#fdf4f0':'#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 26 }}>{m.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: method===m.value?'#c94a1e':'#0f0d0b' }}>{m.label}</div>
                  <div style={{ fontSize: 12, color: '#7a7060', marginTop: 2 }}>{m.sub}</div>
                </div>
                {method === m.value && <div style={{ marginLeft: 'auto', color: '#c94a1e', fontSize: 18 }}>✓</div>}
              </button>
            ))}
          </div>

          {method === 'sms' && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>NÚMERO DE TELÉFONO</label>
              <input style={inputStyle} placeholder="+34 600 000 000" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
          )}

          <button onClick={handleSetup} disabled={loading}
            style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: loading?'#e2dbd0':'#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading?'not-allowed':'pointer' }}>
            {loading ? 'Configurando...' : 'Continuar →'}
          </button>
        </div>
      )}

      {/* Step: TOTP Setup - Show QR */}
      {step === 'setup' && method === 'totp' && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 24 }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 16 }}>Escanea el código QR</div>
          <div style={{ background: '#eaf1fb', borderRadius: 10, padding: '12px 14px', marginBottom: 18, fontSize: 13, color: '#1a4e8c' }}>
            📱 Abre Google Authenticator, Authy o cualquier app TOTP y escanea el código QR.
          </div>
          {qrCode && (
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 12, border: '1px solid #e2dbd0' }} />
            </div>
          )}
          {secret && (
            <div style={{ background: '#f9f6f1', borderRadius: 10, padding: '10px 14px', marginBottom: 18 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 4 }}>Clave manual (si no puedes escanear)</div>
              <div style={{ fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all', color: '#0f0d0b', userSelect: 'all' }}>{secret}</div>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>CÓDIGO DE VERIFICACIÓN</label>
            <input style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 24, textAlign: 'center', letterSpacing: 6 }}
              placeholder="000000" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setStep('select')} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e2dbd0', background: '#fff', fontSize: 14, cursor: 'pointer' }}>← Atrás</button>
            <button onClick={handleConfirm} disabled={loading || code.length < 6}
              style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: (loading||code.length<6)?'#e2dbd0':'#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: (loading||code.length<6)?'not-allowed':'pointer' }}>
              {loading ? 'Verificando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      )}

      {/* Step: Confirm OTP (email/sms) */}
      {step === 'confirm' && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 24 }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 16 }}>Introduce el código</div>
          <div style={{ background: '#eaf4ef', borderRadius: 10, padding: '12px 14px', marginBottom: 18, fontSize: 13, color: '#2d6a4f' }}>
            {method === 'email' ? '📧 Te enviamos un código de 6 dígitos a tu email.' : '💬 Te enviamos un código por SMS.'}
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>CÓDIGO DE 6 DÍGITOS</label>
            <input style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 28, textAlign: 'center', letterSpacing: 8, fontWeight: 700 }}
              placeholder="000000" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setStep('select')} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e2dbd0', background: '#fff', fontSize: 14, cursor: 'pointer' }}>← Atrás</button>
            <button onClick={handleConfirm} disabled={loading || code.length < 6}
              style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: (loading||code.length<6)?'#e2dbd0':'#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: (loading||code.length<6)?'not-allowed':'pointer' }}>
              {loading ? 'Verificando...' : 'Activar 2FA'}
            </button>
          </div>
        </div>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
          <h3 style={{ fontFamily: 'Georgia,serif', fontSize: 22, marginBottom: 8, color: '#2d6a4f' }}>2FA Activado</h3>
          <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 24 }}>Tu cuenta está protegida. Se pedirá el código en cada inicio de sesión.</p>
          <button onClick={() => setStep('select')} style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid #e2dbd0', background: '#fff', fontSize: 14, cursor: 'pointer' }}>
            Cambiar método
          </button>
        </div>
      )}

      {/* Disable 2FA */}
      {status?.enabled && step === 'select' && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 24, marginTop: 16 }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, marginBottom: 14, color: '#9b2c5e' }}>Desactivar 2FA</div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>CÓDIGO ACTUAL PARA CONFIRMAR</label>
            <input style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 20, textAlign: 'center', letterSpacing: 4 }}
              placeholder="000000" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          <button onClick={handleDisable} disabled={loading || code.length < 6}
            style={{ width: '100%', padding: '11px', borderRadius: 10, border: '1px solid #9b2c5e', background: '#faeef4', color: '#9b2c5e', fontSize: 13, fontWeight: 700, cursor: (loading||code.length<6)?'not-allowed':'pointer' }}>
            Desactivar 2FA
          </button>
        </div>
      )}
    </div>
  );
}
// updated
